import React, { createContext, useState, useRef, useContext, ReactNode } from 'react';

// Define the shared transition state type
export interface TransitionItem {
  id: string;
  rect?: DOMRect;
  data?: any; // Additional data needed for the transition
}

interface TransitionContextType {
  // Active transition item
  activeItem: TransitionItem | null;
  // Set active item to transition from
  setActiveItem: (item: TransitionItem | null) => void;
  // Check if an item is currently active
  isActive: (id: string) => boolean;
  // Get transition properties for an element
  getTransitionProps: (id: string) => {
    isActive: boolean;
    rect?: DOMRect;
    data?: any;
  };
  // Register item measurements
  registerItem: (id: string, element: HTMLElement, data?: any) => void;
}

const TransitionContext = createContext<TransitionContextType | undefined>(undefined);

interface TransitionProviderProps {
  children: ReactNode;
}

export const TransitionProvider: React.FC<TransitionProviderProps> = ({ children }) => {
  // State to track the currently active item
  const [activeItem, setActiveItem] = useState<TransitionItem | null>(null);
  
  // Ref to store measurements of registered items
  const itemsRef = useRef<Map<string, TransitionItem>>(new Map());

  // Register an item's measurements
  const registerItem = (id: string, element: HTMLElement, data?: any) => {
    const rect = element.getBoundingClientRect();
    itemsRef.current.set(id, { id, rect, data });
  };

  // Check if an item is active
  const isActive = (id: string): boolean => {
    return activeItem?.id === id;
  };

  // Get transition properties for an element
  const getTransitionProps = (id: string) => {
    const isItemActive = isActive(id);
    const itemData = itemsRef.current.get(id);
    
    return {
      isActive: isItemActive,
      rect: itemData?.rect,
      data: itemData?.data,
    };
  };

  const contextValue = {
    activeItem,
    setActiveItem,
    isActive,
    getTransitionProps,
    registerItem,
  };

  return (
    <TransitionContext.Provider value={contextValue}>
      {children}
    </TransitionContext.Provider>
  );
};

// Hook to use the transition context
export const useTransition = () => {
  const context = useContext(TransitionContext);
  
  // Create a fallback context if none is found to prevent errors
  if (context === undefined) {
    console.warn('useTransition must be used within a TransitionProvider - using fallback context');
    return {
      activeItem: null,
      setActiveItem: () => {},
      isActive: () => false,
      getTransitionProps: () => ({ isActive: false }),
      registerItem: () => {}
    };
  }
  
  return context;
};
